import * as H from 'history'

import {
    ComponentInformation,
    getRegistrationCreators,
    CompositionInformation,
    LayoutApi,
} from 'json-react-layouts'
import { PageMetaDTO } from 'publication-types'
import { init } from 'json-react-layouts-data-loader'

import { RouteResolution, RedirectTo } from 'common/routing/route-resolver'
import { AdPageKind } from 'common/advertising/ad-targeting'
import { Topic } from 'common/client-data-types/topic'
import { RenderTarget } from 'common/context-providers/render-target-context'
import { CardItem } from 'common/client-data-types/card-types'
import { AdTargeting } from 'common/advertising/add-ads'

import { DataLoaderGlobalParams, resources } from '../data/resources'
import { AdState } from '../advertising/ad-state'
import { cacheDataLoad } from './RoutePrimer'
import { AllEvents } from 'common/events/all-events'

export interface AggregatedData {
    type: 'aggregated'
    sources: Array<CurationData | ListingData | GeolocationCurationData>
}

export interface CurationData {
    type: 'curation'
    name: string
    /* The number of items in the curation to skip */
    offset: number
    /** The number of items to return */
    pageSize: number
    hideKicker?: boolean
    hideProfiles?: boolean

    /** Allows a story stored in metadata to be injected into the collection */
    injectMetadataStory?: {
        /** Index the story will be injected into */
        position: number
        metadataKey: string
        /** Controls what card type it will be mapped to before injecting */
        kind: CardItem['cardType']
    }
}

export interface ListingData {
    type: 'listing'
    source?: string
    topics: string[]
    authors?: string[]
    includeSubTopics?: boolean
    excludeTopics?: string[]
    paging: {
        /** page is 1 based numbering */
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
    /** By default listing results are in a reverse chron so latest first order, this orders the results in chronological order */
    sortResultsInChornologicalOrder?: boolean
    /** Filters to sponsored content */
    isSponsored?: boolean
    hideKicker?: boolean
    publicationKind?: 'article' | 'gallery' | 'galleryAndArticle'
    hideProfiles?: boolean
}

export interface GeolocationCurationData {
    type: 'geolocation'
    /* The number of items in the curation to skip */
    offset?: number
    /** The number of items to return */
    pageSize: number
}

export interface PromotionalData {
    type: 'promotional'
    metadataKey: string
    pageSize: number
}

// This is only METADATA about the data required for this layout, not the data itself
// We can preload this information and we should try to keep this data
// structure as light as possible
export interface RouteMeta<Section extends string> {
    /**
     * Page heading / page title, used as the meta title if not overridden and the page heading
     **/
    heading: string

    /** Opt out of the default title template which is %s | ${Section} */
    noMetaTitleTemplate?: boolean

    includeFromSectionInMetaDescription?: boolean

    headerHasBackgroundFill?: boolean
    /* determines if heading (title) is hidden on the page, default is shown */
    hideHeading?: boolean
    /* The page type which will be reported to GA */
    pageType: string
    refreshOnInactive?: boolean
    adTargeting: AdTargeting
    pageMeta?: PageMetaDTO
    section: Section
    socialMeta?: {
        title?: string
        description?: string
    }
    /** Additional props which should be inlcuded in the page loaded event */
    additionalPageProperties?: object
}

export interface PageRouteInformation<
    Sections extends string,
    TComponents extends ComponentInformation<any, any>,
    TCompositions extends CompositionInformation<any, TComponents, any, any>
> extends RouteMeta<Sections> {
    kind: 'page'
    compositions: TCompositions[]
}
export interface RedirectRouteInformation {
    kind: 'redirect'
    isErrorPage?: boolean
    redirectTo: RedirectTo
}

export interface LoadingRouteInformation {
    kind: 'loading'
    section: string | undefined
}

export type RouteInformation<
    Sections extends string,
    TComponents extends ComponentInformation<any, any>,
    TCompositions extends CompositionInformation<any, TComponents, any, any>
> =
    | PageRouteInformation<Sections, TComponents, TCompositions>
    | RedirectRouteInformation
    | LoadingRouteInformation

export type GetRouteAdTargeting = (
    adPageKind: AdPageKind,
    section: string,
    primaryTopicOrPageId: string | Topic,
    ...secondaryTopics: Topic[]
) => AdTargeting

/** Available to route info resolvers */
export interface RouteServices<Resolution extends RouteResolution = RouteResolution>
    extends DataLoaderGlobalParams {
    getAdTargeting: GetRouteAdTargeting
    resolution: Resolution
    location: H.Location
    hostname: string | undefined
    /**
     * On server: Should be `${x-forwarded-proto}:` - not the service protocol which will always be http
     * On client: window.location.protocol
     */
    protocol: string | undefined
    renderTarget: RenderTarget
}

/** Available to all registered components at render time */
export interface ComponentServices<Resolution extends RouteResolution = RouteResolution>
    extends DataLoaderGlobalParams {
    layout: LayoutApi<any, any, ComponentServices, any, any>
    resolution: Resolution
    location: H.Location
    adState: AdState
    onEvent: (event: AllEvents) => void
}

export type StaticRoute<
    Layout extends LayoutApi<any, any, any, any, any> = LayoutApi<any, any, any, any, any>,
    Sections extends string = string,
    Resolution extends RouteResolution = RouteResolution
> = Layout extends LayoutApi<infer Components, infer Compositions, any, any, any>
    ? (
          services: RouteServices<Resolution>
      ) => RouteInformation<Sections, Components, Compositions> | null
    : unknown

export type StaticPageRoute<
    Layout extends LayoutApi<any, any, any, any, any> = LayoutApi<any, any, any, any, any>,
    Sections extends string = string,
    Resolution extends RouteResolution = RouteResolution
> = Layout extends LayoutApi<infer Components, infer Compositions, any, any, any>
    ? (
          services: RouteServices<Resolution>
      ) => PageRouteInformation<Sections, Components, Compositions>
    : unknown

export interface StaticRoutes<
    Layout extends LayoutApi<any, any, any, any, any> = LayoutApi<any, any, any, any, any>,
    Sections extends string = string,
    Resolution extends RouteResolution = RouteResolution
> {
    [key: string]: StaticRoute<Layout, Sections, Resolution>
}

// Exports
export type PageType<
    T extends LayoutApi<any, any, any, any, any>,
    Sections extends string
> = T extends LayoutApi<infer Components, infer Compositions, any, any, any>
    ? PageRouteInformation<Sections, Components, Compositions>
    : unknown

export const {
    createRegisterableComponent,
    createRegisterableComposition,
} = getRegistrationCreators<ComponentServices>()
export const { createRegisterableComponentWithData, middleware: dataLoaderMiddleware } = init<
    ComponentServices
>(resources, cacheDataLoad)

export type GetCompositions<T extends LayoutApi<any, any, any, any, any>> = T extends LayoutApi<
    any,
    infer Compositions,
    any,
    any,
    any
>
    ? Compositions
    : unknown

export type GetComponents<T extends LayoutApi<any, any, any, any, any>> = T extends LayoutApi<
    infer Components,
    any,
    any,
    any,
    any
>
    ? Components
    : unknown

export function createPageDefinition<
    T extends LayoutApi<any, any, any, any, any>,
    Sections extends string
>(
    routeMetadata: RouteMeta<Sections>,
    ...compositions: Array<GetCompositions<T>>
): PageRouteInformation<Sections, GetComponents<T>, GetCompositions<T>> {
    return {
        kind: 'page',
        ...routeMetadata,
        compositions,
    }
}
